import { createContext, PropsWithChildren, useContext, useMemo, useReducer } from 'react';
import { ResourcesEN } from '../translations/ResourcesEN';
import { ResourcesPT } from '../translations/ResourcesPT';
import { TLangs, TResources } from '../translations/types';
import { findObjectPath, iterateObject } from '../utils';

type TContext = {
    resources: TResources;
    language: TLangs;
    changeLanguage: (type: object) => void;
    translate: (name: string) => string;
}

// 1. Create Context
export const TranslationContext = createContext<TContext | null>(null);

// 2. Create custom hook
export const useTranslationContext = () => {
    const context = useContext(TranslationContext);

    if (!context) {
        throw new Error('useTranslationContext must be used within an TranslationProvider');
    }
    return context;
};

enum TLanguages {
    PT = 'PT',
    EN = 'EN',
}

type TState = {
    resource: TResources;
    language: TLangs;
}

type TAction = {
    type: TLanguages;
}

const reducer = (state: TState, action: TAction) => {

    localStorage.setItem('language', action.type)
    document.documentElement.setAttribute("lang", action.type.toLowerCase());

    switch (action.type) {
        case 'PT':
            return { resource: ResourcesPT, language: action.type };
        case 'EN':
            return { resource: ResourcesEN, language: action.type };

        default:
            return state;
    }
}


const handleSelectedResource = (lang: TLangs | string | null) => {
    let state = {} as any;
    switch (lang) {
        case 'PT':
            state = { resource: ResourcesPT, language: lang };
            break;
        case 'EN':
            state = { resource: ResourcesEN, language: lang };
            break;

        default:
            state = { resource: ResourcesPT, language: lang };
            break;
    }

    return [state.resource, state.language];
}

// 3. Create Provider
export const TranslationProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const storageLang = localStorage.getItem('language');
    if (!storageLang) {
        localStorage.setItem('language', document.documentElement.lang.toUpperCase())
    };

    const initialState = handleSelectedResource(storageLang);

    const [state, dispatch] = useReducer(reducer, { resource: initialState[0], language: initialState[1] })

    const value = useMemo(() => {
        const { resource, language } = state;

        /**
         * Function: translate
         * 
         * Uses the resource value itself and finds the right path in the resource
         * object. This path is then used to access to new translated value.
         * 
         * @param key value to translate (needs to exact match with values from ResourceXX file)
         * @returns translated value in required language
         */
        const translate = (key: string) => {
            const resourceKey = iterateObject(ResourcesPT, key);
            const path = findObjectPath(ResourcesPT, resourceKey, key)?.slice(1);

            return path?.split('.').reduce(function (o: { [x: string]: any; }, key: string | number) {
                return o[key];
            }, resource);
        };

        return { resources: resource, language, changeLanguage: dispatch, translate }
    }, [state])

    return <TranslationContext.Provider value={value as any}>{children}</TranslationContext.Provider>;
};
